// TBD: needs some updates


import { Entity } from "../components/entity.js";
import { createSingletonLogger } from "./singleton-logger.js";

class RuntimeComponentLoader {
  static componentName = "runtime-component-loader";

  #logger;

  constructor() {
    this.#logger = createSingletonLogger(this);
  }

  async loadComponent(source, context, type = 'module') {
    this.#logger.log(`Loading component with type: ${type}`);
    
    switch (type) {
      case 'module':
        return await this.#loadModule(source);
      case 'function':
        return this.#loadFunction(source);
      case 'url':
        return await this.#loadFromURL(source);
      case 'code':
        return this.#loadFromCode(source, context);
      default:
        throw new Error(`Unknown component loader type: ${type}`);
    }
  }
  
  async #loadModule(modulePath) {
    try {
      this.#logger.log(`Loading module from path: ${modulePath}`);
      const module = null; // await import(modulePath); TODO: Doesn't work, should be revisited later
      return module.default || module.Component || module;
    } catch (error) {
      this.#logger.error(`Failed to load module from ${modulePath}: ${error.message}`);
      throw error;
    }
  }
  
  #loadFunction(componentClass) {
    if (typeof componentClass !== 'function') {
      throw new Error('Source must be a function/class when using type "function"');
    }
    
    this.#logger.log(`Loading component from function: ${componentClass.name || 'anonymous'}`);
    return componentClass;
  }
  
  async #loadFromURL(url) {
    try {
      this.#logger.log(`Loading component from URL: ${url}`);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch component: ${response.statusText}`);
      }
      
      const code = await response.text();
      return this.#loadFromCode(code);
    } catch (error) {
      this.#logger.error(`Failed to load component from URL ${url}: ${error.message}`);
      throw error;
    }
  }
  
  #loadFromCode(code, context = {}) {
    try {
      this.#logger.log(`Loading component from code string`);
      
      // Create an isolated context for the component code
      const AsyncFunction = Object.getPrototypeOf(async function(){}).constructor;
      
      // Wrap the code to provide necessary imports and exports
      const wrappedCode = `
        const exports = {};
        const module = { exports };
        
        ${code}
        
        return module.exports.default || module.exports.Component || exports.default || exports.Component;
      `;
      
      // Create function with Entity in scope
      const moduleWrapper = new AsyncFunction("Entity", ...Object.keys(context), wrappedCode);
      
      // Execute and return the component class
      return moduleWrapper(Entity, ...Object.values(context));
    } catch (error) {
      this.#logger.error(`Failed to load component from code: ${error.message}`);
      throw error;
    }
  }
  
  validateComponent(ComponentClass) {
    // Basic validation to ensure it's a valid component
    if (typeof ComponentClass !== 'function') {
      throw new Error('Component must be a class or constructor function');
    }
    
    // Check if it has required methods (could be more thorough)
    const instance = new ComponentClass();
    
    if (typeof instance.init !== 'function' && typeof instance.update !== 'function') {
      this.#logger.warn('Component does not have init or update methods - it may not function properly');
    }
    
    return true;
  }
}

const componentLoader = new RuntimeComponentLoader();
export { RuntimeComponentLoader, componentLoader };